import { useGetGalleryQuery } from '../../redux/services/hotel/hotelAPI';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';

const slides = [
  { id: 'hero-1', image: '/media/images/bg1.jpg' },
  { id: 'hero-2', image: '/media/images/bg2.jpg' },
  { id: 'hero-3', image: '/media/images/brown-wood.jpg' },
  { id: 'hero-4', image: '/media/images/black-wood.jpg' },
  { id: 'hero-5', image: '/media/images/download.jpg' },
  { id: 'hero-6', image: '/media/images/Simple Black Gourmet Menu Background Wallpaper Image For Free Download - Pngtree.jpg' },
  { id: 'hero-7', image: '/media/images/Simple Atmospheric Black Striped Background Wallpaper Image For Free Download - Pngtree.jpg' },
  { id: 'hero-8', image: '/media/images/Premium Photo _ Restaurant menu_ top view of woodboard menu laying on the rustic wooden desk with accessories_.jpg' },
];

const Gallery = () => {
  const { data: gallery, isError, isLoading } = useGetGalleryQuery();

  return (
    <section className="relative isolate overflow-hidden bg-slate-950">
      <div className="absolute inset-0 -z-10">
        {isLoading ? (
          <Box sx={{ width: '100%', height: '720px' }}>
            <LinearProgress />
          </Box>
        ) : isError ? (
          <div className="section-shell py-20 text-center text-red-500">Unable to load featured imagery.</div>
        ) : (
          <Carousel showArrows={false} showStatus={false} showThumbs={false} autoPlay infiniteLoop animationHandler="fade" transitionTime={700} className="w-full">
            {(gallery?.length ? gallery : slides).map((image) => (
              <div key={image.id} className="h-[720px] min-h-[620px] w-full">
                <img className="h-full w-full object-cover" src={image.image} alt="GoTrip featured stay" />
              </div>
            ))}
          </Carousel>
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/60 to-slate-950/20" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(59,130,246,0.28),transparent_30%),radial-gradient(circle_at_80%_10%,rgba(245,158,11,0.18),transparent_28%)]" />
      </div>

      <div className="section-shell grid min-h-[720px] items-center gap-12 pt-24 lg:grid-cols-[1.05fr_0.95fr] lg:pt-24">
        <div className="animate-fade-up">
          <div className="mb-6">
            <span className="eyebrow">All-in-one hotel & restaurant booking</span>
          </div>
          <h1 className="display-title text-white">
            Discover and book exceptional stays and dining experiences.
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-200">
            GoTrip brings curated rooms, restaurant reservations, transparent pricing, and trusted reviews into one modern booking flow.
          </p>

          <div className="mt-9 grid gap-3 sm:grid-cols-3 pb-10">
            <Link to="/rooms" className="btn-primary">Book a room</Link>
            <Link to="/foods" className="btn-secondary bg-white text-slate-950 hover:bg-slate-100">Reserve dining</Link>
            <Link to="/contact" className="btn-ghost text-white hover:bg-white/10">Contact support</Link>
          </div>

          <div className="grid max-w-2xl grid-cols-3 gap-3 rounded-3xl border border-white/15 bg-white/10 p-4 backdrop-blur">
            <div>
              <p className="text-2xl font-black text-white">4.8</p>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-300">Avg rating</p>
            </div>
            <div>
              <p className="text-2xl font-black text-white">24/7</p>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-300">Support</p>
            </div>
            <div>
              <p className="text-2xl font-black text-white">Instant</p>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-300">Confirmations</p>
            </div>
          </div>
        </div>

        <div className="animate-fade-up lg:justify-self-end">
          <div className="premium-card bg-white/95 p-5 sm:p-6">
            <h2 className="text-xl font-black tracking-tight text-ink">Start your next booking</h2>
            <p className="mt-2 text-sm leading-6 text-muted">Search stays, compare dining options, and confirm in fewer steps.</p>

            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-muted">Destination</span>
                <input className="field" type="text" placeholder="Where are you going?" />
              </label>
              <label className="block">
                <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-muted">Date</span>
                <input className="field" type="date" />
              </label>
              <label className="block sm:col-span-2">
                <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-muted">Experience</span>
                <select className="field">
                  <option>Hotel stay</option>
                  <option>Restaurant reservation</option>
                  <option>Both</option>
                </select>
              </label>
            </div>

            <Link to="/rooms" className="btn-primary mt-5 w-full">Search availability</Link>

            <div className="mt-5 rounded-2xl bg-gotrip-50 p-4">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full bg-emerald-100 text-emerald-700">
                  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M16.704 5.29a1 1 0 0 1 .006 1.414l-7.25 7.45a1 1 0 0 1-1.436 0l-3.03-3.1a1 1 0 1 1 1.432-1.4l2.314 2.363 6.55-6.717a1 1 0 0 1 1.414-.01Z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-bold text-ink">Trusted booking layer</p>
                  <p className="text-sm leading-6 text-muted">Clear pricing, live availability, and confirmation-first UX.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Gallery;
